import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BadgePercent,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Info,
  Pencil,
  Power,
  PowerOff,
  Plus,
  Search,
  Trash2,
  Users,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import {
  isCouponClientRestrictionEnabled,
  isCouponMultiUseEnabled,
  isCouponValidityPeriodEnabled,
} from "@/features/couponFeature";
import { isCouponItemsEnabled } from "@/features/couponItemsFeature";
import { CouponClientPicker } from "./CouponClientPicker";
import { CouponItemPicker } from "./CouponItemPicker";
import {
  commercialCyclesApi,
  couponApi,
  type CommercialCycle,
  type Coupon,
  type CouponCommentAction,
  type CouponDiscountType,
  type CouponInput,
} from "@/lib/api";
import { formatCurrency } from "@/lib/formatters";

const COUPON_DELETE_VISIBLE = false;

const emptyForm: CouponInput = {
  name: "",
  code: "",
  discountType: isCouponItemsEnabled ? "Items" : "Percentage",
  value: 0,
  cycleYear: null,
  items: [],
  maxUses: 1,
  validFrom: null,
  validUntil: null,
  clientCodes: [],
  comment: "",
};

const COMMENT_ACTION_LABELS: Record<CouponCommentAction, string> = {
  Created: "Criação",
  Updated: "Edição",
  Deactivated: "Desativação",
  Reactivated: "Reativação",
};

type SortKey = "name" | "code" | "value" | "createdBy" | "createdAt" | "client" | "status" | "usage";
type SortDirection = "asc" | "desc";

const PAGE_SIZES = [10, 25, 50];

const couponStatusLabel = (coupon: Coupon) =>
  !coupon.isActive ? "Desativado"
    : coupon.isUsed ? (coupon.maxUses === 1 ? "Utilizado" : "Esgotado")
    : isCouponValidityPeriodEnabled && coupon.validityStatus === "Expired" ? "Expirado"
    : isCouponValidityPeriodEnabled && coupon.validityStatus === "Scheduled" ? "Agendado"
    : coupon.isReserved ? "Em uso em um pedido" : "Disponível";

/** Ciclos que aceitam cupom: abertos ou em fechamento. O padrão é o de maior ano (o mais recente). */
const selectableCycles = (cycles: CommercialCycle[]) =>
  cycles
    .filter((cycle) => cycle.status === "Open" || cycle.status === "Closing")
    .sort((a, b) => b.year - a.year);

const couponTypeLabel = (type: CouponDiscountType) =>
  type === "Items" ? "Por item" : type === "Percentage" ? "Percentual" : "Valor fixo";

const couponSortValue = (coupon: Coupon, key: SortKey): string | number => {
  switch (key) {
    case "name": return coupon.name.toLocaleLowerCase("pt-BR");
    case "code": return coupon.code.toLocaleLowerCase("pt-BR");
    case "value": return couponTypeLabel(coupon.discountType).toLocaleLowerCase("pt-BR");
    case "createdBy": return coupon.createdBy.toLocaleLowerCase("pt-BR");
    case "createdAt": return coupon.createdAt;
    case "client": return (coupon.clients?.[0]?.name || coupon.clientCodes?.[0] || "").toLocaleLowerCase("pt-BR");
    case "status": return couponStatusLabel(coupon).toLocaleLowerCase("pt-BR");
    case "usage": return coupon.usedCount;
  }
};

interface SortableHeadProps {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey;
  direction: SortDirection;
  onSort: (key: SortKey) => void;
  className?: string;
}

function SortableHead({ label, sortKey, activeKey, direction, onSort, className }: SortableHeadProps) {
  const active = activeKey === sortKey;
  const Icon = !active ? ArrowUpDown : direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className} aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 font-medium hover:text-foreground"
      >
        {label}
        <Icon className={`h-3.5 w-3.5 ${active ? "text-foreground" : "text-muted-foreground/60"}`} />
      </button>
    </TableHead>
  );
}

// A vigência trabalha no horário de Brasília (UTC-3, sem horário de verão); o backend guarda o instante em UTC.
const BRASILIA_OFFSET_MS = 3 * 60 * 60 * 1000;
const toBrasiliaInput = (iso?: string | null) =>
  iso ? new Date(new Date(iso).getTime() - BRASILIA_OFFSET_MS).toISOString().slice(0, 16) : null;
const fromBrasiliaInput = (value?: string | null) => (value ? `${value}:00-03:00` : null);
const formatDateTime = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    : null;

const getErrorMessage = (error: unknown) =>
  (error as { response?: { data?: { message?: string } } })?.response?.data
    ?.message ?? "Não foi possível concluir a operação.";

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—";

export default function CouponManagement() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  // Modo "Detalhes": mesmo modal de criação/edição, só para visualizar (cupom já utilizado).
  const [readOnly, setReadOnly] = useState(false);
  const [couponToToggle, setCouponToToggle] = useState<Coupon | null>(null);
  const [toggleComment, setToggleComment] = useState("");
  const [couponToDelete, setCouponToDelete] = useState<Coupon | null>(null);
  const [clientsCoupon, setClientsCoupon] = useState<Coupon | null>(null);
  const [form, setForm] = useState<CouponInput>(emptyForm);

  const cyclesQuery = useQuery({
    queryKey: ["coupon-cycles"],
    queryFn: commercialCyclesApi.list,
    enabled: isCouponItemsEnabled && dialogOpen,
    staleTime: 60_000,
  });
  const availableCycles = useMemo(
    () => selectableCycles([...(cyclesQuery.data?.data ?? [])]),
    [cyclesQuery.data],
  );
  const isItemCoupon = form.discountType === "Items";

  const couponsQuery = useQuery({
    queryKey: ["coupons"],
    queryFn: couponApi.getAll,
    staleTime: 0,
    refetchOnMount: "always", // sempre atualiza ao entrar na tela
    refetchOnWindowFocus: true,
  });
  const [isExporting, setIsExporting] = useState(false);
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await couponApi.exportXlsx();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "cupons.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Não foi possível exportar os cupons",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["coupons"] });

  const saveMutation = useMutation({
    mutationFn: (input: CouponInput) =>
      editingCoupon
        ? couponApi.update(editingCoupon.id, input)
        : couponApi.create(input),
    onSuccess: async () => {
      await refresh();
      setDialogOpen(false);
      toast({
        title: editingCoupon ? "Cupom atualizado" : "Cupom criado",
        description: "As informações foram salvas com sucesso.",
      });
    },
    onError: (error) =>
      toast({
        title: "Não foi possível salvar",
        description: getErrorMessage(error),
        variant: "destructive",
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: couponApi.delete,
    onSuccess: async () => {
      await refresh();
      setCouponToDelete(null);
      toast({
        title: "Cupom excluído",
        description: "O cupom foi removido com sucesso.",
      });
    },
    onError: (error) =>
      toast({
        title: "Não foi possível excluir",
        description: getErrorMessage(error),
        variant: "destructive",
      }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ coupon, comment }: { coupon: Coupon; comment: string }) =>
      couponApi.setActive(coupon.id, !coupon.isActive, comment),
    onSuccess: async (_, { coupon }) => {
      await refresh();
      setCouponToToggle(null);
      setToggleComment("");
      toast({
        title: coupon.isActive ? "Cupom desativado" : "Cupom reativado",
        description: "A alteração foi registrada com o comentário informado.",
      });
    },
    onError: (error) =>
      toast({
        title: "Não foi possível alterar o cupom",
        description: getErrorMessage(error),
        variant: "destructive",
      }),
  });

  const coupons = couponsQuery.data?.data ?? [];
  const filteredCoupons = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("pt-BR");
    if (!query) return coupons;
    return coupons.filter(
      (coupon) =>
        coupon.name.toLocaleLowerCase("pt-BR").includes(query) ||
        coupon.code.toLocaleLowerCase("pt-BR").includes(query) ||
        coupon.createdBy.toLocaleLowerCase("pt-BR").includes(query),
    );
  }, [coupons, search]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection(key === "createdAt" || key === "usage" ? "desc" : "asc");
    }
    setPage(1);
  };

  const sortedCoupons = useMemo(() => {
    const factor = sortDirection === "asc" ? 1 : -1;
    return [...filteredCoupons].sort((a, b) => {
      const left = couponSortValue(a, sortKey);
      const right = couponSortValue(b, sortKey);
      if (typeof left === "number" && typeof right === "number") return (left - right) * factor;
      return String(left).localeCompare(String(right), "pt-BR") * factor;
    });
  }, [filteredCoupons, sortKey, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedCoupons.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedCoupons = sortedCoupons.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const openCreate = () => {
    setEditingCoupon(null);
    setReadOnly(false);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  // Ciclo padrão do cupom por itens: o vigente (aberto/em fechamento); havendo mais de um, o mais recente.
  useEffect(() => {
    if (!dialogOpen || readOnly || !isCouponItemsEnabled || !isItemCoupon || form.cycleYear != null) return;
    if (availableCycles.length > 0) {
      setForm((current) => (current.cycleYear != null ? current : { ...current, cycleYear: availableCycles[0].year }));
    }
  }, [dialogOpen, readOnly, isItemCoupon, availableCycles, form.cycleYear]);

  const openEdit = (coupon: Coupon, viewOnly = false) => {
    if (!viewOnly && (coupon.usedCount > 0 || coupon.isReserved)) return;
    setEditingCoupon(coupon);
    setReadOnly(viewOnly);
    setForm({
      name: coupon.name,
      code: coupon.code,
      discountType: coupon.discountType,
      value: coupon.discountType === "Items" ? 0 : coupon.value,
      cycleYear: coupon.cycleYear ?? null,
      items: (coupon.items ?? []).map((item) => ({ productId: item.productId, quantity: item.quantity })),
      maxUses: coupon.maxUses,
      validFrom: toBrasiliaInput(coupon.validFrom),
      validUntil: toBrasiliaInput(coupon.validUntil),
      clientCodes: coupon.clientCodes ?? [],
      comment: "",
    });
    setDialogOpen(true);
  };

  const submit = () => {
    if (
      form.name.trim().length < 3 ||
      form.code.trim().length < 3 ||
      (!isItemCoupon && form.value <= 0) ||
      (form.maxUses !== null && (!Number.isInteger(form.maxUses) || form.maxUses < 1))
    ) {
      toast({
        title: "Revise os campos",
        description: "Informe nome, código, valor e uma quantidade de usos válida (mínimo 1).",
        variant: "destructive",
      });
      return;
    }
    if (isCouponClientRestrictionEnabled && form.clientCodes.length !== 1) {
      toast({
        title: "Cliente obrigatório",
        description: "Selecione o cliente do cupom.",
        variant: "destructive",
      });
      return;
    }
    if (form.comment.trim().length === 0) {
      toast({
        title: "Comentário obrigatório",
        description: "Informe um comentário para registrar esta alteração. Ele não poderá ser editado depois.",
        variant: "destructive",
      });
      return;
    }
    if (isItemCoupon) {
      if (form.cycleYear == null) {
        toast({ title: "Ciclo obrigatório", description: "Selecione o ciclo do cupom.", variant: "destructive" });
        return;
      }
      const items = form.items ?? [];
      if (items.length === 0 || items.some((item) => !Number.isInteger(item.quantity) || item.quantity < 1)) {
        toast({
          title: "Itens obrigatórios",
          description: "Selecione ao menos um produto e informe a quantidade a bonificar (mínimo 1).",
          variant: "destructive",
        });
        return;
      }
    }
    if (form.discountType === "Percentage" && form.value > 100) {
      toast({
        title: "Percentual inválido",
        description: "O percentual deve ser menor ou igual a 100%.",
        variant: "destructive",
      });
      return;
    }
    if (form.validFrom && form.validUntil && form.validUntil < form.validFrom) {
      toast({
        title: "Período inválido",
        description: "A data e hora finais não podem ser anteriores às iniciais.",
        variant: "destructive",
      });
      return;
    }
    saveMutation.mutate({
      ...form,
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      comment: form.comment.trim(),
      value: isItemCoupon ? 0 : form.value,
      cycleYear: isItemCoupon ? form.cycleYear : null,
      items: isItemCoupon ? form.items : [],
      validFrom: fromBrasiliaInput(form.validFrom),
      validUntil: fromBrasiliaInput(form.validUntil),
    });
  };

  const copyCode = async (code: string) => {
    await navigator.clipboard.writeText(code);
    toast({ title: "Código copiado", description: code });
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <BadgePercent className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">
              Gestão de Cupons
            </h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Crie e acompanhe cupons aplicáveis aos produtos do pedido. Cupons utilizados não podem ser
            editados nem excluídos.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isExporting || couponsQuery.isLoading}
          >
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? "Exportando..." : "Exportar Excel"}
          </Button>
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Novo cupom
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Total de cupons</p>
            <p className="mt-1 text-2xl font-bold">{coupons.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Disponíveis</p>
            <p className="mt-1 text-2xl font-bold text-emerald-600">
              {coupons.filter((coupon) => !coupon.isUsed).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Utilizados</p>
            <p className="mt-1 text-2xl font-bold text-muted-foreground">
              {coupons.filter((coupon) => coupon.isUsed).length}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="border-b">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">Cupons cadastrados</CardTitle>
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
                placeholder="Buscar por nome, código ou criador"
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {couponsQuery.isLoading ? (
            <div className="space-y-3 p-6">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : couponsQuery.isError ? (
            <div className="p-10 text-center">
              <p className="text-sm text-destructive">
                Não foi possível carregar os cupons.
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => couponsQuery.refetch()}
              >
                Tentar novamente
              </Button>
            </div>
          ) : filteredCoupons.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              Nenhum cupom encontrado.
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {isCouponClientRestrictionEnabled && (
                      <SortableHead label="Cliente" sortKey="client" activeKey={sortKey} direction={sortDirection} onSort={handleSort} className="min-w-[13rem]" />
                    )}
                    <SortableHead label="Nome" sortKey="name" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableHead label="Código" sortKey="code" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableHead label="Tipo de cupom" sortKey="value" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableHead label="Criado por" sortKey="createdBy" activeKey={sortKey} direction={sortDirection} onSort={handleSort} className="w-56" />
                    <SortableHead label="Status" sortKey="status" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableHead label="Utilização" sortKey="usage" activeKey={sortKey} direction={sortDirection} onSort={handleSort} className={isCouponMultiUseEnabled ? "w-36" : "w-60"} />
                    <TableHead className="w-32">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedCoupons.map((coupon) => {
                    const clients = coupon.clients ?? [];
                    const locked = coupon.usedCount > 0 || coupon.isReserved;
                    return (
                    <TableRow key={coupon.id}>
                      {isCouponClientRestrictionEnabled && (
                        <TableCell>
                          {clients.length === 0 ? (
                            <span className="text-xs text-muted-foreground">Qualquer cliente</span>
                          ) : clients.length === 1 ? (
                            <span className="text-sm font-medium">{clients[0].name || clients[0].code}</span>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 gap-1.5 border-secondary bg-secondary text-secondary-foreground hover:bg-background hover:text-secondary"
                              onClick={() => setClientsCoupon(coupon)}
                            >
                              <Users className="h-3.5 w-3.5" />
                              {clients.length} clientes
                            </Button>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="font-medium">{coupon.name}</div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          className="h-auto p-0 font-mono font-semibold"
                          onClick={() => copyCode(coupon.code)}
                        >
                          {coupon.code}
                          <Copy className="ml-2 h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{couponTypeLabel(coupon.discountType)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[14rem] break-all text-xs font-medium">
                          {coupon.createdBy}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(coupon.createdAt)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={coupon.isUsed ? "secondary" : "default"}>
                          {couponStatusLabel(coupon)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {!isCouponMultiUseEnabled ? (
                          <div className="max-w-[14rem] text-xs">
                            {coupon.usages[0] ? (
                              <>
                                <div className="break-all font-medium">{coupon.usages[0].usedBy}</div>
                                <div className="text-muted-foreground">{formatDateTime(coupon.usages[0].usedAt)}</div>
                              </>
                            ) : (
                              <span className="text-muted-foreground">Ainda não utilizado</span>
                            )}
                            {isCouponValidityPeriodEnabled && coupon.validUntil && (
                              <div className="mt-0.5 text-muted-foreground">
                                Válido até {formatDateTime(coupon.validUntil)}
                              </div>
                            )}
                          </div>
                        ) : (
                        <div className="max-w-[9rem] text-sm">
                          <div className="font-medium">
                            {coupon.maxUses === null
                              ? `${coupon.usedCount} ${coupon.usedCount === 1 ? "uso" : "usos"} (ilimitado)`
                              : `${coupon.usedCount} de ${coupon.maxUses} ${coupon.maxUses === 1 ? "uso" : "usos"}`}
                          </div>
                          {coupon.usages.length === 0 && (
                            <span className="text-muted-foreground">
                              Ainda não utilizado
                            </span>
                          )}
                          {isCouponValidityPeriodEnabled && coupon.validUntil && (
                            <div className="mt-0.5 text-xs text-muted-foreground">
                              Válido até {formatDateTime(coupon.validUntil)}
                            </div>
                          )}
                        </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-start gap-1">
                          {coupon.usedCount > 0 ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Detalhes"
                              aria-label="Detalhes"
                              onClick={() => openEdit(coupon, true)}
                            >
                              <Info className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              title={locked ? "Cupom em uso em um pedido: não pode ser editado agora" : "Editar"}
                              aria-label="Editar"
                              disabled={locked}
                              onClick={() => openEdit(coupon)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {(isCouponMultiUseEnabled || isCouponItemsEnabled) && (
                            <span title={coupon.isUsed ? "Cupom já utilizado: não pode ser desativado" : undefined}>
                              <Button
                                variant="ghost"
                                size="icon"
                                title={coupon.isUsed ? undefined : coupon.isActive ? "Desativar" : "Ativar"}
                                aria-label={coupon.isActive ? "Desativar" : "Ativar"}
                                disabled={coupon.isUsed}
                                onClick={() => {
                                  setToggleComment("");
                                  setCouponToToggle(coupon);
                                }}
                              >
                                {coupon.isActive ? <PowerOff className="h-4 w-4 text-destructive" /> : <Power className="h-4 w-4 text-primary" />}
                              </Button>
                            </span>
                          )}
                          {/* Exclusão oculta na listagem: o cupom é desativado em vez de excluído. */}
                          {COUPON_DELETE_VISIBLE && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              title={locked ? "Cupom já utilizado: não pode ser excluído" : "Excluir"}
                              aria-label="Excluir"
                              disabled={locked}
                              onClick={() => setCouponToDelete(coupon)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <span>Linhas por página</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((size) => (
                      <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span>
                  {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, sortedCoupons.length)} de {sortedCoupons.length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
                  <ChevronLeft className="mr-1 h-4 w-4" /> Anterior
                </Button>
                <span className="text-muted-foreground">Página {currentPage} de {totalPages}</span>
                <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}>
                  Próxima <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={clientsCoupon !== null} onOpenChange={(open) => !open && setClientsCoupon(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Clientes elegíveis</DialogTitle>
            <DialogDescription>
              {clientsCoupon ? `${clientsCoupon.name} · ${clientsCoupon.code}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto rounded-md border">
            {(clientsCoupon?.clients ?? []).map((client) => (
              <div key={client.code} className="flex items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0">
                <span className="font-mono text-xs">{client.code}</span>
                <span className="min-w-0 flex-1 truncate">{client.name || "—"}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClientsCoupon(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Criação/edição/detalhes em painel lateral (mesmo padrão da solicitação de divergência do pedido). */}
      <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[720px]">
          <SheetHeader className="space-y-1 border-b px-6 py-5 pr-12 text-left sm:text-left">
            <SheetTitle className="text-xl font-bold leading-tight">
              {readOnly ? "Detalhes do cupom" : editingCoupon ? "Editar cupom" : "Novo cupom"}
            </SheetTitle>
            {!readOnly && (
              <SheetDescription className="text-sm">
                O código será informado pelo usuário na tela de resumo do pedido.
              </SheetDescription>
            )}
          </SheetHeader>
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <fieldset disabled={readOnly} className="m-0 grid min-w-0 gap-4 border-0 p-0 py-2">
            <div className="grid gap-2">
              <Label htmlFor="coupon-name">Nome</Label>
              <Input
                id="coupon-name"
                value={form.name}
                maxLength={100}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                placeholder="Ex.: Campanha de outubro"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="coupon-code">Código do cupom</Label>
              <Input
                id="coupon-code"
                value={form.code}
                maxLength={40}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    code: event.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9_-]/g, ""),
                  }))
                }
                placeholder="Ex.: OUTUBRO10"
                className="font-mono uppercase"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Tipo de cupom</Label>
                <Select
                  value={form.discountType}
                  onValueChange={(value: CouponDiscountType) =>
                    setForm((current) => ({
                      ...current,
                      discountType: value,
                      value: value === current.discountType ? current.value : 0,
                      items: value === current.discountType ? current.items : [],
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FixedAmount">Valor fixo</SelectItem>
                    <SelectItem value="Percentage">Percentual</SelectItem>
                    {isCouponItemsEnabled && <SelectItem value="Items">Por item</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {!isItemCoupon && (
                <div className="grid gap-2">
                  <Label htmlFor="coupon-value">
                    Valor {form.discountType === "Percentage" ? "(%)" : "(R$)"}
                  </Label>
                  <Input
                    id="coupon-value"
                    type="number"
                    min="0.01"
                    max={form.discountType === "Percentage" ? 100 : undefined}
                    step="0.01"
                    placeholder={form.discountType === "Percentage" ? "Ex.: 10 (%)" : "Ex.: 150,00 (R$)"}
                    value={form.value || ""}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, value: Number(event.target.value) }))
                    }
                  />
                </div>
              )}
            </div>
            {isItemCoupon && (
              <div className="grid gap-2">
                <Label>Ciclo (obrigatório)</Label>
                <Select
                  value={form.cycleYear != null ? String(form.cycleYear) : ""}
                  onValueChange={(value) =>
                    setForm((current) => ({ ...current, cycleYear: Number(value), items: [] }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={cyclesQuery.isLoading ? "Carregando ciclos..." : "Selecione o ciclo"} />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      ...availableCycles.map((cycle) => cycle.year),
                      ...(form.cycleYear != null && !availableCycles.some((cycle) => cycle.year === form.cycleYear)
                        ? [form.cycleYear]
                        : []),
                    ].map((year) => (
                      <SelectItem key={year} value={String(year)}>
                        Ciclo {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isCouponMultiUseEnabled && (
              <>
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="coupon-max-uses">Quantidade de usos</Label>
                <div className="flex items-center gap-2">
                  <Switch
                    id="coupon-unlimited"
                    checked={form.maxUses === null}
                    onCheckedChange={(checked) =>
                      setForm((current) => ({ ...current, maxUses: checked ? null : 1 }))
                    }
                  />
                  <Label htmlFor="coupon-unlimited" className="text-xs font-normal">
                    Ilimitado
                  </Label>
                </div>
              </div>
              <Input
                id="coupon-max-uses"
                type="number"
                min="1"
                step="1"
                disabled={form.maxUses === null}
                value={form.maxUses ?? ""}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    maxUses: Math.floor(Number(event.target.value)),
                  }))
                }
              />
              {!readOnly && (
                <p className="text-xs text-muted-foreground">
                  Padrão 1 (uso único). Após o primeiro uso o cupom não pode mais ser editado ou excluído, apenas desativado.
                </p>
              )}
            </div>
              </>
            )}
            {isCouponValidityPeriodEnabled && (
              <>
            {/* "Válido a partir de" fica oculto: vale a partir da criação do cupom (valor já salvo é preservado na edição). */}
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="coupon-valid-until">Válido até</Label>
                <Input
                  id="coupon-valid-until"
                  type="datetime-local"
                  value={form.validUntil ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, validUntil: event.target.value || null }))
                  }
                />
              </div>
            </div>
            {!readOnly && (
              <p className="-mt-2 text-xs text-muted-foreground">
                Data e hora no horário de Brasília. Em branco = sem limite.
              </p>
            )}
              </>
            )}
            {isCouponClientRestrictionEnabled && (
              <>
            <div className="grid gap-2">
                <Label>Cliente (obrigatório)</Label>
              <CouponClientPicker
                value={form.clientCodes}
                initialNames={Object.fromEntries((editingCoupon?.clients ?? []).map((client) => [client.code, client.name]))}
                hideHint={readOnly}
                required
                onChange={(clientCodes) => setForm((current) => ({ ...current, clientCodes, items: [] }))}
              />
            </div>
              </>
            )}
            {!readOnly && (
              <div className="grid gap-2">
                <Label htmlFor="coupon-comment">Comentário (obrigatório)</Label>
                <Textarea
                  id="coupon-comment"
                  value={form.comment}
                  maxLength={1000}
                  rows={3}
                  onChange={(event) => setForm((current) => ({ ...current, comment: event.target.value }))}
                  placeholder="Informe o ticket dessa solicitação"
                />
              </div>
            )}
            {isItemCoupon && (
              <div className="grid gap-2">
                <Label>Itens bonificados (obrigatório)</Label>
                {form.cycleYear == null || form.clientCodes.length !== 1 ? (
                  <p className="text-sm text-muted-foreground">
                    Selecione o ciclo e o cliente para listar os produtos do perfil.
                  </p>
                ) : (
                  <CouponItemPicker
                    clientCode={form.clientCodes[0]}
                    cycleYear={form.cycleYear}
                    value={form.items ?? []}
                    savedItems={editingCoupon?.items}
                    readOnly={readOnly}
                    onChange={(items) => setForm((current) => ({ ...current, items }))}
                  />
                )}
              </div>
            )}
          </fieldset>
          {(editingCoupon?.comments?.length ?? 0) > 0 && (
            <div className="grid gap-2">
              <Label>Histórico de comentários</Label>
              <div className="space-y-4">
                {editingCoupon?.comments?.map((item, index) => (
                  <div key={`${item.createdAt}-${index}`} className="rounded-md border p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium break-all">{item.createdBy}</div>
                        <div className="text-xs text-muted-foreground">{formatDateTime(item.createdAt)}</div>
                      </div>
                      <div className="text-right text-sm">
                        <div className="font-semibold">{COMMENT_ACTION_LABELS[item.action] ?? item.action}</div>
                      </div>
                    </div>
                    <p className="mt-3 whitespace-pre-wrap border-t pt-3 text-sm">{item.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {readOnly && editingCoupon && (
            <div className="grid gap-2">
              <Label>Utilização</Label>
              <div className="space-y-4">
                {editingCoupon.usages.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Este cupom ainda não foi utilizado.</p>
                ) : (
                  editingCoupon.usages.map((usage, index) => (

                  <div key={`${usage.usedAt}-${index}`} className="rounded-md border p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium break-all">{usage.usedBy}</div>
                        {usage.clientCode && (
                          <div className="text-xs text-muted-foreground">
                            Cliente {usage.clientCode}
                            {usage.clientName ? ` · ${usage.clientName}` : ""}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground">{formatDate(usage.usedAt)}</div>
                      </div>
                      <div className="text-right text-sm">
                        <div className="font-semibold">{formatCurrency(usage.discountAmount)}</div>
                        <div className="text-xs text-muted-foreground">desconto total</div>
                        {(usage.scheduledDiscountAmount ?? 0) > 0 && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            + {formatCurrency(usage.scheduledDiscountAmount ?? 0)} em programados
                            {" "}({formatCurrency(usage.scheduledAppliedAmount ?? 0)} já abatidos)
                          </div>
                        )}
                      </div>
                    </div>
                    <Table className="mt-3">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Pedido</TableHead>
                          <TableHead className="text-right">Valor do pedido</TableHead>
                          <TableHead className="text-right">Desconto no pedido</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {usage.orders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell className="font-mono text-xs">{order.orderNumber}</TableCell>
                            <TableCell className="text-right">
                              {order.totalAmount != null ? formatCurrency(order.totalAmount) : "—"}
                            </TableCell>
                            <TableCell className="text-right">{formatCurrency(order.discountAmount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  ))
                )}
              </div>
            </div>
          )}
          </div>
          <SheetFooter className="items-center gap-2 border-t px-6 py-4 sm:justify-end sm:space-x-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {readOnly ? "Fechar" : "Cancelar"}
            </Button>
            {!readOnly && (
              <Button onClick={submit} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? "Salvando..." : "Salvar cupom"}
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Dialog
        open={couponToToggle !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCouponToToggle(null);
            setToggleComment("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{couponToToggle?.isActive ? "Desativar cupom" : "Reativar cupom"}</DialogTitle>
            <DialogDescription>
              {couponToToggle?.isActive
                ? <>O cupom <strong>{couponToToggle?.code}</strong> deixará de ser aceito em novos pedidos. O histórico é preservado.</>
                : <>O cupom <strong>{couponToToggle?.code}</strong> voltará a ser aceito em novos pedidos.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="coupon-toggle-comment">Comentário (obrigatório)</Label>
            <Textarea
              id="coupon-toggle-comment"
              value={toggleComment}
              maxLength={1000}
              rows={3}
              onChange={(event) => setToggleComment(event.target.value)}
              placeholder="Explique o motivo desta alteração"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCouponToToggle(null)}>Cancelar</Button>
            <Button
              disabled={toggleMutation.isPending || toggleComment.trim().length === 0}
              onClick={() => couponToToggle && toggleMutation.mutate({ coupon: couponToToggle, comment: toggleComment.trim() })}
            >
              {toggleMutation.isPending ? "Salvando..." : couponToToggle?.isActive ? "Desativar cupom" : "Reativar cupom"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!couponToDelete}
        onOpenChange={(open) => !open && setCouponToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cupom?</AlertDialogTitle>
            <AlertDialogDescription>
              O cupom <strong>{couponToDelete?.code}</strong> será removido.
              Essa ação só é permitida porque ele ainda não foi utilizado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() =>
                couponToDelete && deleteMutation.mutate(couponToDelete.id)
              }
            >
              Excluir cupom
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
